import { useState, useEffect } from 'react';
import { Star, Send, Loader2, User, Pencil, Trash2, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface Review {
  id: string;
  rating: number;
  comment: string | null;
  admin_reply: string | null;
  created_at: string;
  user_id: string;
  profiles: {
    first_name: string | null;
    username: string | null;
    avatar_url: string | null;
  } | null;
}

interface ProductReviewsProps {
  productId: string;
  productName: string;
}

export const StarRating = ({ 
  rating, 
  size = 'md', 
  interactive = false,
  onRate 
}: { 
  rating: number; 
  size?: 'sm' | 'md' | 'lg';
  interactive?: boolean;
  onRate?: (rating: number) => void;
}) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-6 h-6',
  };

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => onRate?.(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={cn(
            "transition-colors",
            interactive && "cursor-pointer hover:scale-110"
          )}
        >
          <Star
            className={cn(
              sizeClasses[size],
              (interactive ? hoverRating || rating : rating) >= star
                ? "fill-yellow-500 text-yellow-500"
                : "text-muted-foreground/30"
            )}
          />
        </button>
      ))}
    </div>
  );
};

export const ProductReviews = ({ productId, productName }: ProductReviewsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [newRating, setNewRating] = useState(0);
  const [newComment, setNewComment] = useState('');
  
  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRating, setEditRating] = useState(0);
  const [editComment, setEditComment] = useState('');

  useEffect(() => {
    fetchReviews();
    if (user) {
      checkCanReview();
    }
  }, [productId, user]);

  const fetchReviews = async () => {
    try {
      // First fetch reviews without profile join
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('id, rating, comment, admin_reply, created_at, user_id')
        .eq('product_id', productId)
        .order('created_at', { ascending: false });

      if (reviewsError) throw reviewsError;

      if (!reviewsData || reviewsData.length === 0) {
        setReviews([]);
        setLoading(false);
        return;
      }

      // Get unique user IDs
      const userIds = [...new Set(reviewsData.map(r => r.user_id))];

      // Fetch profiles for these users
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, first_name, username, avatar_url')
        .in('id', userIds);

      // Create a map of user_id to profile
      const profilesMap = new Map(
        (profilesData || []).map(p => [p.id, p])
      );

      // Transform data to match Review interface
      const transformedReviews: Review[] = reviewsData.map(r => ({
        id: r.id,
        rating: r.rating,
        comment: r.comment,
        admin_reply: r.admin_reply || null,
        created_at: r.created_at,
        user_id: r.user_id,
        profiles: profilesMap.get(r.user_id) || null,
      }));
      
      setReviews(transformedReviews);
      
      // Check if current user already reviewed
      if (user) {
        setHasReviewed(transformedReviews.some(r => r.user_id === user.id));
      }
    } catch (error) {
      logger.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCanReview = async () => {
    if (!user) {
      setCanReview(false);
      return;
    }

    try {
      // Check if user has ordered this product with completed status
      const { data, error } = await supabase
        .from('order_items')
        .select(`
          id,
          orders!inner (
            user_id,
            status
          )
        `)
        .eq('product_id', productId)
        .eq('orders.user_id', user.id)
        .eq('orders.status', 'completed')
        .limit(1);

      if (error) throw error;
      setCanReview((data?.length || 0) > 0);
    } catch (error) {
      logger.error('Error checking review eligibility:', error);
      setCanReview(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!user || newRating === 0) {
      toast({
        title: 'Ошибка',
        description: 'Выберите оценку от 1 до 5 звёзд',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('reviews')
        .insert({
          product_id: productId,
          user_id: user.id,
          rating: newRating,
          comment: newComment.trim() || null,
        });

      if (error) throw error;

      toast({
        title: 'Отзыв добавлен',
        description: 'Спасибо за вашу оценку!',
      });

      setNewRating(0);
      setNewComment('');
      setHasReviewed(true);
      fetchReviews();
    } catch (error: any) {
      logger.error('Error submitting review:', error);
      toast({
        title: 'Ошибка',
        description: error.message || 'Не удалось добавить отзыв',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditReview = (review: Review) => {
    setEditingId(review.id);
    setEditRating(review.rating);
    setEditComment(review.comment || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditRating(0);
    setEditComment('');
  };

  const handleSaveEdit = async () => {
    if (!editingId || editRating === 0) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('reviews')
        .update({
          rating: editRating,
          comment: editComment.trim() || null,
        })
        .eq('id', editingId)
        .eq('user_id', user?.id);

      if (error) throw error;

      toast({
        title: 'Отзыв обновлён',
        description: 'Ваши изменения сохранены',
      });

      setEditingId(null);
      fetchReviews();
    } catch (error: any) {
      logger.error('Error updating review:', error);
      toast({
        title: 'Ошибка',
        description: error.message || 'Не удалось обновить отзыв',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId)
        .eq('user_id', user?.id);

      if (error) throw error;

      toast({
        title: 'Отзыв удалён',
      });

      setHasReviewed(false);
      fetchReviews();
    } catch (error: any) {
      logger.error('Error deleting review:', error);
      toast({
        title: 'Ошибка',
        description: error.message || 'Не удалось удалить отзыв',
        variant: 'destructive',
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Review Form */}
      {user && canReview && !hasReviewed && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-lg bg-muted/50 border border-border space-y-4"
        >
          <h4 className="font-medium">Оставить отзыв</h4>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Ваша оценка:</span>
            <StarRating rating={newRating} size="lg" interactive onRate={setNewRating} />
          </div>

          <Textarea
            placeholder="Напишите ваш отзыв (необязательно)..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="resize-none"
            rows={3}
          />

          <Button 
            onClick={handleSubmitReview} 
            disabled={submitting || newRating === 0}
            className="gap-2"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Отправить отзыв
          </Button>
        </motion.div>
      )}

      {/* Info messages */}
      {!user && (
        <div className="text-center py-4 text-muted-foreground text-sm">
          Войдите в аккаунт, чтобы оставить отзыв
        </div>
      )}

      {user && !canReview && !hasReviewed && (
        <div className="text-center py-4 text-muted-foreground text-sm">
          Оставить отзыв могут только покупатели, получившие этот товар
        </div>
      )}

      {user && hasReviewed && (
        <div className="text-center py-4 text-eco text-sm">
          ✓ Вы уже оставили отзыв на этот товар
        </div>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Пока нет отзывов. Будьте первым!
        </div>
      ) : (
        <ScrollArea className="h-[300px]">
          <div className="space-y-4 pr-4">
            <AnimatePresence>
              {reviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 rounded-lg bg-muted/30 border border-border/50"
                >
                  {editingId === review.id ? (
                    // Edit mode
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Оценка:</span>
                        <StarRating rating={editRating} size="lg" interactive onRate={setEditRating} />
                      </div>
                      <Textarea
                        value={editComment}
                        onChange={(e) => setEditComment(e.target.value)}
                        placeholder="Ваш отзыв..."
                        className="resize-none"
                        rows={2}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={submitting || editRating === 0}
                          className="gap-1"
                        >
                          {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                          Сохранить
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelEdit}
                          className="gap-1"
                        >
                          <X className="h-3 w-3" />
                          Отмена
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                        {review.profiles?.avatar_url ? (
                          <img 
                            src={review.profiles.avatar_url} 
                            alt="" 
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <User className="w-5 h-5 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="font-medium text-sm truncate">
                            {review.profiles?.first_name || review.profiles?.username || 'Покупатель'}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground shrink-0">
                              {formatDate(review.created_at)}
                            </span>
                            {user && user.id === review.user_id && (
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => handleEditReview(review)}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-destructive hover:text-destructive"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Удалить отзыв?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Это действие нельзя отменить. Отзыв будет удалён навсегда.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Отмена</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDeleteReview(review.id)}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        Удалить
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            )}
                          </div>
                        </div>
                        <StarRating rating={review.rating} size="sm" />
                        {review.comment && (
                          <p className="mt-2 text-sm text-muted-foreground">
                            {review.comment}
                          </p>
                        )}
                        {review.admin_reply && (
                          <div className="mt-3 pl-3 border-l-2 border-primary/30 bg-muted/30 rounded-r-lg p-2">
                            <div className="flex items-center gap-1 mb-1">
                              <span className="text-xs font-medium text-primary">Ответ продавца</span>
                            </div>
                            <p className="text-sm text-muted-foreground">{review.admin_reply}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

// Hook to fetch average rating for a product
export const useProductRating = (productId: string) => {
  const [rating, setRating] = useState<{ average: number; count: number } | null>(null);

  useEffect(() => {
    const fetchRating = async () => {
      try {
        const { data, error } = await supabase
          .from('reviews')
          .select('rating')
          .eq('product_id', productId);

        if (error) throw error;

        if (data && data.length > 0) {
          const sum = data.reduce((acc, r) => acc + r.rating, 0);
          setRating({
            average: Math.round((sum / data.length) * 10) / 10,
            count: data.length,
          });
        } else {
          setRating({ average: 0, count: 0 });
        }
      } catch (error) {
        logger.error('Error fetching rating:', error);
        setRating({ average: 0, count: 0 });
      }
    };

    fetchRating();
  }, [productId]);

  return rating;
};